/** Zillow filter cleanup card (Tom 2026-08-12: "make the button"). Preview the
 * Zillow leads that match none of your jobs' filters, then remove them. The
 * scraper cron does the work with the crawler's own filter code, so this UI just
 * requests and polls. */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  requestZillowPurge, getZillowPurgeStatus, purgeReasonLabel,
  type PurgeStatus,
} from "./purgeApi";

export function ZillowFilterPurge({ token, isAdmin }: { token: string | null; isAdmin: boolean }) {
  const [st, setSt] = useState<PurgeStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [confirmApply, setConfirmApply] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPoll = useCallback(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
  }, []);

  const poll = useCallback(async () => {
    try {
      const s = await getZillowPurgeStatus(token);
      setSt(s);
      if (s.status === "done" || s.status === "error" || s.status === "idle") {
        stopPoll(); setBusy(false);
      }
    } catch { /* keep polling; transient */ }
  }, [token, stopPoll]);

  // load once so a report from a previous session is visible
  useEffect(() => { if (isAdmin) void poll(); return stopPoll; }, [isAdmin, poll, stopPoll]);

  const start = async (mode: "preview" | "apply") => {
    if (busy) return;
    setBusy(true); setNote(null); setConfirmApply(false);
    try {
      await requestZillowPurge(token, mode);
      setSt({ status: "pending", mode });
      stopPoll();
      timer.current = setInterval(() => void poll(), 6000);
    } catch (e) {
      setBusy(false);
      setNote((e as { detail?: string; message?: string })?.detail ?? (e as Error)?.message ?? "Request failed");
    }
  };

  if (!isAdmin) return null;

  const rep = st?.report;
  const running = st?.status === "pending" || st?.status === "running" || busy;
  const wouldRemove = rep?.removed ?? 0;
  const isPreview = rep && !rep.applied;

  return (
    <div className="zfpurge">
      <div className="zfphd">
        <div>
          <b>Clean up off-target Zillow leads</b>
          <p className="sub">Re-checks every Zillow lead against both your jobs' keywords &amp; criteria and removes the ones that match neither. Keeps anything you've worked. Removal is recoverable.</p>
        </div>
        <button className="btn" disabled={running} onClick={() => void start("preview")}>
          {running ? "Scanning…" : "Preview off-target leads"}
        </button>
      </div>

      {running ? (
        <div className="zfpnote">Scanning on the next crawler cycle — this can take a few minutes. The result appears here automatically.</div>
      ) : null}

      {st?.status === "error" || rep?.error ? (
        <div className="zfpwarn">Couldn't run: {st?.error || rep?.error}</div>
      ) : null}

      {rep && !rep.error ? (
        <div className="zfpreport">
          <div className="zfprow">
            <span className="zfpbig">{(rep.checked ?? 0).toLocaleString()}</span> Zillow leads checked ·{" "}
            <b>{rep.applied ? "removed" : "would remove"} {wouldRemove.toLocaleString()}</b>
            {" "}· kept {(rep.keptMatch ?? 0).toLocaleString()} matching,
            {" "}{(rep.keptTouched ?? 0).toLocaleString()} you've worked,
            {" "}{(rep.keptNoDescription ?? 0).toLocaleString()} unchecked (no description)
          </div>
          {rep.reasons && rep.reasons.length ? (
            <table className="zfptable">
              <thead><tr><th>Reason</th><th>Count</th><th>Examples</th></tr></thead>
              <tbody>
                {rep.reasons.map((r) => (
                  <tr key={r.rule}>
                    <td>{purgeReasonLabel(r.rule)}</td>
                    <td>{r.count.toLocaleString()}</td>
                    <td className="zfpex">{(r.samples || []).slice(0, 3).join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="zfpnote">Nothing to remove — every Zillow lead matches a job. ✓</div>}

          {isPreview && wouldRemove > 0 ? (
            confirmApply ? (
              <div className="zfpconfirm">
                <span>Remove {wouldRemove.toLocaleString()} leads? They're tombstoned (recoverable), and anything you've worked is kept.</span>
                <button className="btn danger" disabled={running} onClick={() => void start("apply")}>Yes, remove {wouldRemove.toLocaleString()}</button>
                <button className="btn ghost" onClick={() => setConfirmApply(false)}>Cancel</button>
              </div>
            ) : (
              <button className="btn danger" onClick={() => setConfirmApply(true)}>Remove these {wouldRemove.toLocaleString()} leads…</button>
            )
          ) : null}
          {rep.applied ? <div className="zfpnote on">Removed {wouldRemove.toLocaleString()} leads ✓ (recoverable). Run a preview again anytime.</div> : null}
        </div>
      ) : null}

      {note ? <div className="zfpwarn">{note}</div> : null}
    </div>
  );
}
